import { IView } from '../../../../Assembler/dist/registry/iface/iView.js';
import { JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';

// Type prefix constants for new format
const TYPE_HTML = 's';
const META_KEY = '_meta';

/**
 * HtmlJson entry value type - string or nested object structure
 */
type HtmlJsonValue = string | HtmlJsonObject;

/**
 * HtmlJson object type - plain object with string keys
 */
interface HtmlJsonObject {
    [key: string]: HtmlJsonValue | HtmlJsonMeta;
}

/**
 * Metadata structure for HtmlJson
 */
interface HtmlJsonMeta {
    hasJsonKeys: boolean;
    jsonKeys: string[];
    hasPlaceholders: boolean;
    placeholders: string[];
    hasSlotholders: boolean;
    slotholders: string[];
    hasArrayBlocks: boolean;
    arrayBlocks: string[];
}

/**
 * Checks if this is a type-prefixed key (new format: type char + digit, e.g., "k2_Title", "s1")
 */
function isTypePrefixedKey(key: string): boolean {
    return key.length >= 2 && /\d/.test(key[1]);
}

/**
 * Base implementation of IView and IViewJson
 * Implements both interfaces - engine will prefer getViewJson for Json engines (avoids conversion overhead)
 */
export class BaseView extends IView {
    /**
     * Gets the full type name for DynamicView operation tracking
     */
    get typeName(): string {
        return 'BaseView';
    }

    /**
     * Gets dynamic view for template processing
     */
    getView(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentHtml: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        configJson: JsonObject | null = null
    ): string {
        let retHtml = componentHtml;

        try {
            // Apply ReplaceKey/ReplaceText replacement if specified in paramJson
            retHtml = BaseView.applyReplacement(retHtml, paramJson);

            // Try to get ComponentID from paramJson first, then componentJson, then fall back to camelCase componentName
            const divId = BaseView.getComponentId(paramJson, componentJson, componentName);

            // Get ComponentGroup from paramJson/componentJson or default to "Main"
            const refreshGroup = BaseView.getComponentGroup(paramJson, componentJson);

            // Build optional attributes
            const optionalAttrs = BaseView.buildOptionalAttributes(paramJson, componentJson);

            // Wrap componentHtml with a div having the component id, refresh group, depth, and optional attributes
            const landmarkAttrs = componentDepth === 0 ? ` role="region" aria-label="${componentName}"` : '';
            retHtml = `<div id="${divId}" data-refresh-group="${refreshGroup}" data-component-depth="${componentDepth}"${optionalAttrs}${landmarkAttrs}>${retHtml}</div>`;
        } catch (ex) {
            Logger.error((ex as Error).message, 'BaseView');
        }

        return retHtml;
    }

    /**
     * Gets dynamic view wrapper for HtmlJson structure (avoids HTML string conversion)
     */
    getViewJson(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentHtmlJson: JsonObject,
        paramJson: JsonObject,
        componentJson: JsonObject,
        configJson: JsonObject | null = null
    ): JsonObject {
        try {
            // Get ComponentID from paramJson first, then componentJson, then fall back to camelCase componentName
            const divId = BaseView.getComponentId(paramJson, componentJson, componentName);

            // Get ComponentGroup from paramJson/componentJson or default to "Main"
            const refreshGroup = BaseView.getComponentGroup(paramJson, componentJson);

            // Build optional attributes
            const optionalAttrs = BaseView.buildOptionalAttributes(paramJson, componentJson);

            // Create new object with wrapper using new format
            const result = new JsonObject();
            let sequence = 1;

            // Preserve _meta for JSON merge to work correctly
            if (componentHtmlJson.has(META_KEY)) {
                result.set(META_KEY, componentHtmlJson.get(META_KEY));
            }

            // Prepend opening wrapper div with id, refresh group, depth, and optional attributes (new format: s1)
            const landmarkAttrs = componentDepth === 0 ? ` role="region" aria-label="${componentName}"` : '';
            result.set(`${TYPE_HTML}${sequence}`, `<div id="${divId}" data-refresh-group="${refreshGroup}" data-component-depth="${componentDepth}"${optionalAttrs}${landmarkAttrs}>`);
            sequence++;

            // Copy existing entries with renumbered keys
            for (const key of componentHtmlJson.keys()) {
                // Skip metadata
                if (key === META_KEY) continue;

                // Get the type character and name from the key
                let typeChar: string;
                let keyName: string;

                if (isTypePrefixedKey(key)) {
                    // New format: first char is type, after underscore is name
                    typeChar = key[0];
                    const underscoreIdx = key.indexOf('_');
                    keyName = underscoreIdx >= 0 ? key.substring(underscoreIdx + 1) : '';
                } else {
                    // Old format: extract type from suffix after underscore (e.g., "001_Html" -> "Html")
                    const underscoreIndex = key.indexOf('_');
                    const keyType = underscoreIndex >= 0 ? key.substring(underscoreIndex + 1) : key;

                    // Map old format type to new format type char
                    if (keyType.startsWith('Html')) {
                        typeChar = TYPE_HTML;
                        keyName = '';
                    } else if (keyType.startsWith('JsonKey')) {
                        typeChar = 'k';
                        keyName = keyType.substring('JsonKey_'.length);
                    } else if (keyType.startsWith('Placeholder')) {
                        typeChar = 'p';
                        keyName = keyType.substring('Placeholder_'.length);
                    } else if (keyType.startsWith('Slotholder')) {
                        typeChar = 'h';
                        keyName = keyType.substring('Slotholder_'.length);
                    } else if (keyType.startsWith('ArrayBlock')) {
                        typeChar = 'a';
                        keyName = keyType.substring('ArrayBlock_'.length);
                    } else if (keyType.startsWith('Slot_')) {
                        typeChar = 'i';
                        keyName = keyType.substring('Slot_'.length);
                    } else {
                        typeChar = TYPE_HTML;
                        keyName = '';
                    }
                }

                // Apply ReplaceKey/ReplaceText if the value is a string
                const jsonVal: JsonValue = componentHtmlJson.get(key);
                const strVal = jsonVal.asString();
                const newValue: unknown = strVal !== null ? BaseView.applyReplacement(strVal, paramJson) : jsonVal;

                // Create new key in new format
                const newKey = keyName ? `${typeChar}${sequence}_${keyName}` : `${typeChar}${sequence}`;
                result.set(newKey, newValue);
                sequence++;
            }

            // Append closing wrapper div (new format: s{sequence})
            result.set(`${TYPE_HTML}${sequence}`, '</div>');

            return result;
        } catch (ex) {
            Logger.error((ex as Error).message, 'BaseView.GetViewJson');
            return componentHtmlJson;
        }
    }

    /**
     * Applies ReplaceKey/ReplaceText replacement from paramJson to the HTML content
     */
    static applyReplacement(html: string, paramJson: JsonObject): string {
        if (!paramJson) {
            return html;
        }

        // Check if both ReplaceKey and ReplaceText are specified
        if (!paramJson.containsKey('ReplaceKey') || !paramJson.containsKey('ReplaceText')) {
            return html;
        }

        const replaceKey = paramJson.getString('ReplaceKey');
        const replaceText = paramJson.getString('ReplaceText') || '';

        if (!replaceKey || replaceKey.length === 0) {
            return html;
        }

        // Replace all occurrences of the key with the text
        return html.split(replaceKey).join(replaceText);
    }

    /**
     * Gets the component ID from paramJson, componentJson, or falls back to camelCase componentName
     */
    static getComponentId(paramJson: JsonObject, componentJson: JsonObject, componentName: string): string {
        // Try paramJson first
        if (paramJson && paramJson.containsKey('ComponentID')) {
            const componentId = paramJson.getString('ComponentID');
            if (componentId && componentId.length > 0) {
                return componentId;
            }
        }

        // Try componentJson second
        if (componentJson && componentJson.containsKey('ComponentID')) {
            const componentId = componentJson.getString('ComponentID');
            if (componentId && componentId.length > 0) {
                return componentId;
            }
        }

        // Fall back to camelCase componentName
        return BaseView.toCamelCase(componentName);
    }

    /**
     * Gets the component group from paramJson, componentJson, or falls back to "Main"
     */
    static getComponentGroup(paramJson: JsonObject, componentJson: JsonObject): string {
        // Try paramJson first
        if (paramJson && paramJson.containsKey('ComponentGroup')) {
            const componentGroup = paramJson.getString('ComponentGroup');
            if (componentGroup && componentGroup.length > 0) {
                return componentGroup;
            }
        }

        // Try componentJson second
        if (componentJson && componentJson.containsKey('ComponentGroup')) {
            const componentGroup = componentJson.getString('ComponentGroup');
            if (componentGroup && componentGroup.length > 0) {
                return componentGroup;
            }
        }

        // Fall back to "Main"
        return 'Main';
    }

    /**
     * Builds optional attributes string for the wrapper div
     * Supports: EngineType, EngineSlot, RefreshAppSite, MergeID, TimingID
     * Every attribute is emitted only when the component declares it, so a component that declares
     * none renders exactly as it did before any of them existed.
     */
    static buildOptionalAttributes(paramJson: JsonObject, componentJson: JsonObject): string {
        let attrs = '';

        // EngineType -> data-engine-type
        const engineType = BaseView.getOptionalValue('EngineType', paramJson, componentJson);
        if (engineType) {
            attrs += ` data-engine-type="${engineType}"`;
        }

        // EngineSlot -> data-engine-slot (POSITION in a multi-engine comparison, not an engine name).
        // Slot 0 renders whichever engine is currently selected, slot N the Nth engine after it in the
        // ConfigModeType radio order, wrapping. Declaring the position rather than the engine is what
        // lets the SAME component serve a 2-engine surface (Web/Basic) and a 4-engine one (WebJson):
        // the engine is resolved from the live radio list, so it can never disagree with the selection.
        const engineSlot = BaseView.getOptionalValue('EngineSlot', paramJson, componentJson);
        if (engineSlot) {
            attrs += ` data-engine-slot="${engineSlot}"`;
        }

        // RefreshAppSite -> data-refresh-appsite
        const refreshAppSite = BaseView.getOptionalValue('RefreshAppSite', paramJson, componentJson);
        if (refreshAppSite) {
            attrs += ` data-refresh-appsite="${refreshAppSite}"`;
        }

        // MergeID -> data-merge-id (target element ID for innerHTML replacement)
        const mergeId = BaseView.getOptionalValue('MergeID', paramJson, componentJson);
        if (mergeId) {
            attrs += ` data-merge-id="${mergeId}"`;
        }

        // TimingID -> data-timing-id (element that receives this component's timing readout).
        // Declared rather than derived: the client used to guess the timing element by rewriting the
        // merge target's id suffix, which silently resolved to the WRONG element for any component
        // whose merge target did not follow that one naming convention.
        const timingId = BaseView.getOptionalValue('TimingID', paramJson, componentJson);
        if (timingId) {
            attrs += ` data-timing-id="${timingId}"`;
        }

        return attrs;
    }

    /**
     * Gets an optional value from paramJson first, then componentJson
     * Returns empty string if not found
     */
    static getOptionalValue(key: string, paramJson: JsonObject, componentJson: JsonObject): string {
        // Try paramJson first
        if (paramJson && paramJson.containsKey(key)) {
            const value = paramJson.getString(key);
            if (value && value.length > 0) {
                return value;
            }
        }

        // Try componentJson second
        if (componentJson && componentJson.containsKey(key)) {
            const value = componentJson.getString(key);
            if (value && value.length > 0) {
                return value;
            }
        }

        return '';
    }

    /**
     * Converts a string to camelCase (first letter lowercase, rest unchanged)
     */
    static toCamelCase(str: string): string {
        if (!str || str.length === 0) {
            return str;
        }

        // If first character is already lowercase, return as is
        if (str[0] === str[0].toLowerCase()) {
            return str;
        }

        // Convert first character to lowercase
        return str[0].toLowerCase() + str.substring(1);
    }
}
